import { useState, useRef, useEffect, useCallback } from "react";
import JSZip from "jszip";
import { useAuth } from "../hooks/useAuth";
import { api, uploadDocument } from "../utils/api";
import AdSlot from "../components/AdSlot";

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

// ─── Toolbar Button ───────────────────────────────────────────────────────────
function ToolBtn({ icon, label, onClick, active, disabled, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      style={{
        background: active
          ? "linear-gradient(135deg,#3b82f6,#6366f1)"
          : danger
            ? "rgba(239,68,68,0.1)"
            : "rgba(255,255,255,0.04)",
        color: disabled
          ? "#333"
          : active
            ? "#fff"
            : danger
              ? "#f87171"
              : "#cbd5e1",
        border: `1px solid ${active ? "#3b82f6" : danger ? "rgba(239,68,68,0.3)" : "rgba(255,255,255,0.08)"}`,
        borderRadius: 8,
        padding: "6px 13px",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "'Sora',sans-serif",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        gap: 5,
        whiteSpace: "nowrap",
        transition: "all 0.15s",
        userSelect: "none",
      }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function Sep() {
  return (
    <div
      style={{
        width: 1,
        height: 28,
        background: "rgba(255,255,255,0.08)",
        margin: "0 2px",
      }}
    />
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
function Modal({ title, children, onClose }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        zIndex: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          background: "#0f1223",
          border: "1px solid rgba(59,130,246,0.25)",
          borderRadius: 16,
          padding: 28,
          minWidth: 360,
          maxWidth: "90vw",
          boxShadow: "0 24px 64px rgba(0,0,0,0.8)",
          fontFamily: "'Sora',sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16, color: "#93c5fd" }}>
            {title}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#666",
              fontSize: 22,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Upgrade Modal ─────────────────────────────────────────────────────────────
function UpgradeModal({ onClose }) {
  return (
    <Modal title="🚀 Upgrade to Pro" onClose={onClose}>
      <div
        style={{
          color: "#9ca3af",
          fontSize: 14,
          lineHeight: 1.7,
          marginBottom: 20,
        }}
      >
        You've used all{" "}
        <strong style={{ color: "#f59e0b" }}>5 free exports</strong> this month.
        <br />
        Upgrade to <strong style={{ color: "#3b82f6" }}>Pro</strong> for
        unlimited exports.
      </div>
      <div
        style={{
          background: "rgba(59,130,246,0.08)",
          border: "1px solid rgba(59,130,246,0.2)",
          borderRadius: 12,
          padding: 20,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            fontSize: 28,
            fontWeight: 800,
            color: "#fff",
            marginBottom: 4,
          }}
        >
          $9<span style={{ fontSize: 14, color: "#6b7280" }}>/month</span>
        </div>
        {[
          "✅ Unlimited PDF exports",
          "✅ Priority support",
          "✅ No ads",
          "✅ Larger file sizes (up to 200MB)",
        ].map((f) => (
          <div key={f} style={{ fontSize: 13, color: "#93c5fd", marginTop: 6 }}>
            {f}
          </div>
        ))}
      </div>
      <a
        href="mailto:your@email.com?subject=FreePDFMaster.in Pro Upgrade"
        style={{
          display: "block",
          textAlign: "center",
          padding: "12px",
          background: "linear-gradient(135deg,#3b82f6,#6366f1)",
          color: "#fff",
          borderRadius: 10,
          fontWeight: 700,
          textDecoration: "none",
          fontSize: 15,
        }}
      >
        Upgrade to Pro →
      </a>
      <div
        style={{
          marginTop: 12,
          textAlign: "center",
          fontSize: 11,
          color: "#374151",
        }}
      >
        Contact us to set up payment · your@email.com
      </div>
    </Modal>
  );
}

// ─── Thumbnail ────────────────────────────────────────────────────────────────
function ThumbCanvas({ srcCanvas }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c || !srcCanvas) return;
    const scale = 178 / srcCanvas.width;
    c.width = 178;
    c.height = srcCanvas.height * scale;
    c.getContext("2d").drawImage(srcCanvas, 0, 0, c.width, c.height);
  }, [srcCanvas]);
  return <canvas ref={ref} style={{ display: "block", width: "100%" }} />;
}

// ─── Main Editor (Multi‑Document) ─────────────────────────────────────────────
export default function EditorPage() {
  const { user, logout, refreshUser } = useAuth();

  // Multi‑document state
  const [documents, setDocuments] = useState([]); // [{ id, name, pages }]
  const [currentDoc, setCurrentDoc] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [tool, setTool] = useState("select");
  const [annotations, setAnnotations] = useState({});
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [lockPassword, setLockPassword] = useState("");
  const [showLockModal, setShowLockModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockInput, setUnlockInput] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [dragState, setDragState] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [editingText, setEditingText] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [fontSize, setFontSize] = useState(16);
  const [fontColor, setFontColor] = useState("#1e293b");
  const [cropImage, setCropImage] = useState(null);
  const [cropRect, setCropRect] = useState(null);
  const [cropDragging, setCropDragging] = useState(false);
  const [imageForInsert, setImageForInsert] = useState(null);
  const [rectColor, setRectColor] = useState("#000000");
  const [rectDrawState, setRectDrawState] = useState(null);
  const [zoom, setZoom] = useState(1.0);
  const [status, setStatus] = useState("Upload a PDF to start editing");
  const [uploading, setUploading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const pageCanvasRef = useRef(null);
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);
  const imgInputRef = useRef(null);
  const textInputRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const cropImgRef = useRef(null);
  const cropMouseDownRef = useRef(null);

  // Load PDF.js
  const [pdfJsReady, setPdfJsReady] = useState(!!window.pdfjsLib);
  useEffect(() => {
    if (window.pdfjsLib) {
      setPdfJsReady(true);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      setPdfJsReady(true);
    };
    document.head.appendChild(s);
  }, []);

  // Load jsPDF for PDF export
  const loadJsPdf = () =>
    new Promise((resolve, reject) => {
      if (window.jspdf) {
        resolve(window.jspdf.jsPDF);
        return;
      }
      const s = document.createElement("script");
      s.src =
        "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = () => resolve(window.jspdf.jsPDF);
      s.onerror = () => reject(new Error("Failed to load jsPDF"));
      document.head.appendChild(s);
    });

  // Close export menu on outside click
  useEffect(() => {
    if (!showExportMenu) return;
    const handler = () => setShowExportMenu(false);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showExportMenu]);

  // Upload PDF (multi‑doc)
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setStatus("Rendering pages…");
    try {
      const buffer = await file.arrayBuffer();
      const lib = window.pdfjsLib;
      if (!lib) {
        setStatus("PDF.js still loading — please try again in a moment.");
        setUploading(false);
        return;
      }
      const doc = await lib.getDocument({ data: buffer }).promise;
      const rendered = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const vp = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width;
        canvas.height = vp.height;
        await page.render({
          canvasContext: canvas.getContext("2d"),
          viewport: vp,
        }).promise;
        rendered.push({ canvas, width: vp.width, height: vp.height });
      }
      const docId = generateId();
      setDocuments((docs) => [
        ...docs,
        { id: docId, name: file.name, pages: rendered },
      ]);
      setCurrentDoc(docId);
      setPages(rendered);
      setCurrentPage(0);
      setAnnotations({});
      setSelected(null);
      setLocked(false);
      setStatus(`✓ Loaded ${doc.numPages} page(s)`);
    } catch (err) {
      setStatus("Failed to read PDF: " + err.message);
      setUploading(false);
      return;
    }
    setUploading(false);

    // Upload to server for storage and tracking
    try {
      if (!user?.email) throw new Error("User not logged in");
      await uploadDocument(file, user.email);
      setStatus((s) => s + `\n✓ Uploaded ${file.name} to server`);
    } catch (err) {
      setStatus((s) => s + `\nServer upload failed: ${err.message}`);
    }
  };

  // Switch document
  const handleSwitchDoc = (docId) => {
    const doc = documents.find((d) => d.id === docId);
    if (!doc) return;
    setCurrentDoc(docId);
    setPages(doc.pages);
    setCurrentPage(0);
    setAnnotations({});
    setSelected(null);
    setLocked(false);
    setStatus(`Loaded ${doc.name}`);
  };

  // Remove document from view (server copy is preserved)
  const handleRemoveDoc = (e, docId) => {
    e.stopPropagation();
    const remaining = documents.filter((d) => d.id !== docId);
    setDocuments(remaining);
    if (currentDoc === docId) {
      if (remaining.length > 0) {
        const next = remaining[0];
        setCurrentDoc(next.id);
        setPages(next.pages);
        setCurrentPage(0);
        setAnnotations({});
        setSelected(null);
        setLocked(false);
        setStatus(`Switched to ${next.name}`);
      } else {
        setCurrentDoc(null);
        setPages([]);
        setCurrentPage(0);
        setAnnotations({});
        setSelected(null);
        setLocked(false);
        setStatus("Upload a PDF to start editing");
      }
    }
  };

  // Merge all documents
  const handleMergeDocs = () => {
    if (documents.length < 2) return;
    const mergedPages = documents.flatMap((d) => d.pages);
    const docId = generateId();
    setDocuments((docs) => [
      ...docs,
      { id: docId, name: "Merged Document.pdf", pages: mergedPages },
    ]);
    setCurrentDoc(docId);
    setPages(mergedPages);
    setCurrentPage(0);
    setAnnotations({});
    setSelected(null);
    setLocked(false);
    setStatus("✓ Merged all PDFs");
  };

  // Draw PDF page
  useEffect(() => {
    const c = pageCanvasRef.current;
    if (!c || !pages[currentPage]) return;
    const { canvas: src, width, height } = pages[currentPage];
    c.width = width * zoom;
    c.height = height * zoom;
    const ctx = c.getContext("2d");
    ctx.save();
    ctx.scale(zoom, zoom);
    ctx.drawImage(src, 0, 0);
    ctx.restore();
  }, [currentPage, pages, zoom]);

  // Draw annotations overlay
  const drawOverlay = useCallback(() => {
    const overlay = overlayRef.current;
    if (!overlay || !pages[currentPage]) return;
    const { width, height } = pages[currentPage];
    overlay.width = width * zoom;
    overlay.height = height * zoom;
    const ctx = overlay.getContext("2d");
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    (annotations[currentPage] || []).forEach((ann) => {
      ctx.save();
      ctx.scale(zoom, zoom);
      if (ann.type === "text") {
        const sel = selected?.annId === ann.id;
        if (sel) {
          ctx.strokeStyle = "#3b82f6";
          ctx.lineWidth = 1.5 / zoom;
          ctx.setLineDash([5, 3]);
          ctx.strokeRect(
            ann.x - 4,
            ann.y - ann.fontSize - 4,
            ann.width + 8,
            ann.height + 8,
          );
          ctx.setLineDash([]);
          ctx.fillStyle = "#3b82f6";
          ctx.fillRect(
            ann.x + ann.width + 2,
            ann.y + ann.height - ann.fontSize - 2,
            10,
            10,
          );
        }
        if (editingText !== ann.id) {
          ctx.font = `${ann.fontSize}px 'Georgia', serif`;
          ctx.fillStyle = ann.color;
          let line = "",
            lineY = ann.y;
          ann.text.split(" ").forEach((word) => {
            const test = line ? line + " " + word : word;
            if (ctx.measureText(test).width > ann.width && line) {
              ctx.fillText(line, ann.x, lineY);
              line = word;
              lineY += ann.fontSize * 1.4;
            } else line = test;
          });
          if (line) ctx.fillText(line, ann.x, lineY);
        }
      } else if (ann.type === "image" && ann.img) {
        ctx.drawImage(ann.img, ann.x, ann.y, ann.width, ann.height);
        if (selected?.annId === ann.id) {
          ctx.strokeStyle = "#f59e0b";
          ctx.lineWidth = 1.5 / zoom;
          ctx.setLineDash([5, 3]);
          ctx.strokeRect(ann.x - 2, ann.y - 2, ann.width + 4, ann.height + 4);
          ctx.setLineDash([]);
          ctx.fillStyle = "#f59e0b";
          ctx.fillRect(ann.x + ann.width - 6, ann.y + ann.height - 6, 12, 12);
        }
      } else if (ann.type === "rect") {
        ctx.fillStyle = ann.fillColor || "#000000";
        ctx.fillRect(ann.x, ann.y, ann.width, ann.height);
        if (selected?.annId === ann.id) {
          ctx.strokeStyle = "#f59e0b";
          ctx.lineWidth = 1.5 / zoom;
          ctx.setLineDash([5, 3]);
          ctx.strokeRect(ann.x - 2, ann.y - 2, ann.width + 4, ann.height + 4);
          ctx.setLineDash([]);
          ctx.fillStyle = "#f59e0b";
          ctx.fillRect(ann.x + ann.width - 6, ann.y + ann.height - 6, 12, 12);
        }
      }
      ctx.restore();
    });
    // Live preview while drawing a rect
    if (rectDrawState && rectDrawState.w > 0 && rectDrawState.h > 0) {
      ctx.save();
      ctx.scale(zoom, zoom);
      ctx.globalAlpha = 0.6;
      ctx.fillStyle = rectColor;
      ctx.fillRect(
        rectDrawState.x,
        rectDrawState.y,
        rectDrawState.w,
        rectDrawState.h,
      );
      ctx.globalAlpha = 1;
      ctx.strokeStyle = "#f59e0b";
      ctx.lineWidth = 1.5 / zoom;
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(
        rectDrawState.x,
        rectDrawState.y,
        rectDrawState.w,
        rectDrawState.h,
      );
      ctx.setLineDash([]);
      ctx.restore();
    }
  }, [
    annotations,
    currentPage,
    selected,
    editingText,
    pages,
    zoom,
    rectDrawState,
    rectColor,
  ]);

  useEffect(() => {
    drawOverlay();
  }, [drawOverlay]);

  const getPos = (e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom,
    };
  };

  const getAnn = (x, y) => {
    const anns = annotations[currentPage] || [];
    for (let i = anns.length - 1; i >= 0; i--) {
      const a = anns[i];
      const aY = a.type === "text" ? a.y - a.fontSize : a.y;
      if (
        x >= a.x - 4 &&
        x <= a.x + a.width + 4 &&
        y >= aY - 4 &&
        y <= aY + a.height + 4
      )
        return a;
    }
    return null;
  };

  const isResize = (x, y, ann) => {
    if (!ann) return false;
    if (ann.type === "text") {
      const hx = ann.x + ann.width + 2,
        hy = ann.y + ann.height - ann.fontSize - 2;
      return x >= hx && x <= hx + 10 && y >= hy && y <= hy + 10;
    }
    if (ann.type === "image" || ann.type === "rect") {
      const hx = ann.x + ann.width - 6,
        hy = ann.y + ann.height - 6;
      return x >= hx && x <= hx + 12 && y >= hy && y <= hy + 12;
    }
    return false;
  };

  const handleMouseDown = (e) => {
    if (locked) return;
    const { x, y } = getPos(e);
    if (tool === "select") {
      const ann = getAnn(x, y);
      if (ann) {
        setSelected({ annId: ann.id });
        if (isResize(x, y, ann))
          setResizeState({
            annId: ann.id,
            startX: x,
            startY: y,
            origW: ann.width,
            origH: ann.height,
          });
        else
          setDragState({
            annId: ann.id,
            offX: x - ann.x,
            offY: y - (ann.type === "text" ? ann.y - ann.fontSize : ann.y),
          });
      } else setSelected(null);
    } else if (tool === "text") {
      const id = generateId();
      const ann = {
        id,
        type: "text",
        x,
        y: y + fontSize,
        text: "Double-click to edit",
        fontSize,
        color: fontColor,
        width: 220,
        height: fontSize,
      };
      setAnnotations((p) => ({
        ...p,
        [currentPage]: [...(p[currentPage] || []), ann],
      }));
      setSelected({ annId: id });
      setEditingText(id);
      setEditingValue("Double-click to edit");
      setTool("select");
    } else if (tool === "delete") {
      const ann = getAnn(x, y);
      if (ann) {
        setAnnotations((p) => ({
          ...p,
          [currentPage]: (p[currentPage] || []).filter((a) => a.id !== ann.id),
        }));
        setSelected(null);
      }
    } else if (tool === "rect") {
      setRectDrawState({ startX: x, startY: y, x, y, w: 0, h: 0 });
      setSelected(null);
    } else if (tool === "image" && imageForInsert) {
      const img = new Image();
      img.onload = () => {
        const id = generateId();
        const ann = {
          id,
          type: "image",
          x,
          y,
          img,
          width: Math.min(img.naturalWidth, 280),
          height: Math.min(img.naturalHeight, 280),
        };
        setAnnotations((p) => ({
          ...p,
          [currentPage]: [...(p[currentPage] || []), ann],
        }));
        setSelected({ annId: id });
        setTool("select");
        setImageForInsert(null);
      };
      img.src = imageForInsert;
    }
  };

  const handleMouseMove = (e) => {
    if (locked) return;
    const { x, y } = getPos(e);
    if (dragState) {
      setAnnotations((p) => ({
        ...p,
        [currentPage]: (p[currentPage] || []).map((a) =>
          a.id !== dragState.annId
            ? a
            : a.type === "text"
              ? {
                  ...a,
                  x: x - dragState.offX,
                  y: y - dragState.offY + a.fontSize,
                }
              : { ...a, x: x - dragState.offX, y: y - dragState.offY },
        ),
      }));
    }
    if (resizeState) {
      const dx = x - resizeState.startX,
        dy = y - resizeState.startY;
      setAnnotations((p) => ({
        ...p,
        [currentPage]: (p[currentPage] || []).map((a) =>
          a.id !== resizeState.annId
            ? a
            : {
                ...a,
                width: Math.max(60, resizeState.origW + dx),
                height: Math.max(20, resizeState.origH + dy),
              },
        ),
      }));
    }
    if (rectDrawState) {
      const w = x - rectDrawState.startX,
        h = y - rectDrawState.startY;
      setRectDrawState((r) => ({
        ...r,
        x: w >= 0 ? r.startX : x,
        y: h >= 0 ? r.startY : y,
        w: Math.abs(w),
        h: Math.abs(h),
      }));
    }
  };

  const handleMouseUp = () => {
    setDragState(null);
    setResizeState(null);
    if (rectDrawState) {
      if (rectDrawState.w > 5 && rectDrawState.h > 5) {
        const id = generateId();
        const ann = {
          id,
          type: "rect",
          x: rectDrawState.x,
          y: rectDrawState.y,
          width: rectDrawState.w,
          height: rectDrawState.h,
          fillColor: rectColor,
        };
        setAnnotations((p) => ({
          ...p,
          [currentPage]: [...(p[currentPage] || []), ann],
        }));
        setSelected({ annId: id });
      }
      setRectDrawState(null);
    }
  };

  const commitText = () => {
    if (!editingText) return;
    setAnnotations((p) => ({
      ...p,
      [currentPage]: (p[currentPage] || []).map((a) =>
        a.id === editingText ? { ...a, text: editingValue } : a,
      ),
    }));
    setEditingText(null);
  };

  const handleDblClick = (e) => {
    if (locked) return;
    const { x, y } = getPos(e);
    const ann = getAnn(x, y);
    if (ann?.type === "text") {
      setEditingText(ann.id);
      setEditingValue(ann.text);
      setSelected({ annId: ann.id });
      setTimeout(() => textInputRef.current?.focus(), 50);
    }
  };

  // Image crop
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropImage({ src: ev.target.result });
      setCropRect(null);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!cropImage) return;
    const canvas = cropCanvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      cropImgRef.current = img;
      const scale = Math.min(540 / img.width, 380 / img.height, 1);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = cropImage.src;
  }, [cropImage]);

  useEffect(() => {
    const canvas = cropCanvasRef.current;
    const img = cropImgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    if (cropRect) {
      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.clearRect(cropRect.x, cropRect.y, cropRect.w, cropRect.h);
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(cropRect.x, cropRect.y, cropRect.w, cropRect.h);
      ctx.setLineDash([]);
      ctx.drawImage(
        img,
        (cropRect.x / canvas.width) * img.width,
        (cropRect.y / canvas.height) * img.height,
        (cropRect.w / canvas.width) * img.width,
        (cropRect.h / canvas.height) * img.height,
        cropRect.x,
        cropRect.y,
        cropRect.w,
        cropRect.h,
      );
    }
  }, [cropRect]);

  const applyCrop = () => {
    const canvas = cropCanvasRef.current;
    const img = cropImgRef.current;
    if (!canvas || !img) return;
    const out = document.createElement("canvas");
    if (cropRect && cropRect.w > 5 && cropRect.h > 5) {
      const sx = img.width / canvas.width,
        sy = img.height / canvas.height;
      out.width = cropRect.w * sx;
      out.height = cropRect.h * sy;
      out
        .getContext("2d")
        .drawImage(
          img,
          cropRect.x * sx,
          cropRect.y * sy,
          out.width,
          out.height,
          0,
          0,
          out.width,
          out.height,
        );
    } else {
      out.width = img.width;
      out.height = img.height;
      out.getContext("2d").drawImage(img, 0, 0);
    }
    setImageForInsert(out.toDataURL());
    setCropImage(null);
    setTool("image");
    setStatus("Click on the page to place the image");
  };

  // Export helpers
  const buildExportCanvas = (pageIdx = currentPage) => {
    const { canvas: src, width, height } = pages[pageIdx];
    const out = document.createElement("canvas");
    out.width = width;
    out.height = height;
    const ctx = out.getContext("2d");
    ctx.drawImage(src, 0, 0);
    (annotations[pageIdx] || []).forEach((ann) => {
      if (ann.type === "rect") {
        ctx.fillStyle = ann.fillColor || "#000000";
        ctx.fillRect(ann.x, ann.y, ann.width, ann.height);
      } else if (ann.type === "text") {
        ctx.font = `${ann.fontSize}px Georgia,serif`;
        ctx.fillStyle = ann.color;
        let line = "",
          lineY = ann.y;
        ann.text.split(" ").forEach((w) => {
          const t = line ? line + " " + w : w;
          if (ctx.measureText(t).width > ann.width && line) {
            ctx.fillText(line, ann.x, lineY);
            line = w;
            lineY += ann.fontSize * 1.4;
          } else line = t;
        });
        if (line) ctx.fillText(line, ann.x, lineY);
      } else if (ann.type === "image" && ann.img) {
        ctx.drawImage(ann.img, ann.x, ann.y, ann.width, ann.height);
      }
    });
    return out;
  };

  const trackExport = async () => {
    try {
      const result = await api("/pdf/export-complete", { method: "POST" });
      await refreshUser();
      setStatus(
        `✓ Exported · ${result.plan === "free" ? `${result.monthlyEdits}/5 free exports used` : "Unlimited"}`,
      );
    } catch (err) {
      if (err.limitReached) setShowUpgrade(true);
    }
  };

  const handleExportImage = async () => {
    if (!pages[currentPage]) return;
    setShowExportMenu(false);
    if (user?.plan === "free" && (user?.monthlyEdits || 0) >= 5) {
      setShowUpgrade(true);
      return;
    }
    const out = buildExportCanvas();
    const link = document.createElement("a");
    link.download = `edited-page-${currentPage + 1}.png`;
    link.href = out.toDataURL("image/png");
    link.click();
    setStatus("✓ Exported as Image");
    trackExport();
  };

  const handleExportPdf = async () => {
    if (!pages[currentPage]) return;
    setShowExportMenu(false);
    if (user?.plan === "free" && (user?.monthlyEdits || 0) >= 5) {
      setShowUpgrade(true);
      return;
    }
    setStatus("Preparing PDF…");
    try {
      const JsPDF = await loadJsPdf();
      const out = buildExportCanvas();
      const { width, height } = pages[currentPage];
      const pxToMm = (px) => (px * 25.4) / 96;
      const wMm = pxToMm(width),
        hMm = pxToMm(height);
      const orientation = wMm > hMm ? "l" : "p";
      const doc = new JsPDF({ orientation, unit: "mm", format: [wMm, hMm] });
      doc.addImage(out.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, wMm, hMm);
      doc.save(`edited-page-${currentPage + 1}.pdf`);
      setStatus("✓ Exported as PDF");
      trackExport();
    } catch (err) {
      setStatus("PDF export failed: " + err.message);
    }
  };

  const handleExportAllPdf = async () => {
    if (!pages.length) return;
    setShowExportMenu(false);
    if (user?.plan === "free" && (user?.monthlyEdits || 0) >= 5) {
      setShowUpgrade(true);
      return;
    }
    setStatus("Preparing full PDF…");
    try {
      const JsPDF = await loadJsPdf();
      const pxToMm = (px) => (px * 25.4) / 96;
      const { width, height } = pages[0];
      const wMm = pxToMm(width),
        hMm = pxToMm(height);
      const orientation = wMm > hMm ? "l" : "p";
      const doc = new JsPDF({ orientation, unit: "mm", format: [wMm, hMm] });
      for (let i = 0; i < pages.length; i++) {
        if (i > 0) doc.addPage([wMm, hMm], orientation);
        const out = buildExportCanvas(i);
        doc.addImage(out.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, wMm, hMm);
      }
      doc.save(`complete-document.pdf`);
      setStatus("✓ Exported all pages as PDF");
      trackExport();
    } catch (err) {
      setStatus("PDF export failed: " + err.message);
    }
  };

  const handleExportAllZip = async () => {
    if (!pages.length) return;
    setShowExportMenu(false);
    if (user?.plan === "free" && (user?.monthlyEdits || 0) >= 5) {
      setShowUpgrade(true);
      return;
    }
    setStatus("Preparing ZIP of images…");
    try {
      const zip = new JSZip();
      for (let i = 0; i < pages.length; i++) {
        const out = buildExportCanvas(i);
        const dataUrl = out.toDataURL("image/png");
        const base64 = dataUrl.split(",")[1];
        zip.file(`page-${i + 1}.png`, base64, { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "pdf-pages.zip";
      link.click();
      setStatus("✓ Exported all pages as ZIP");
      trackExport();
    } catch (err) {
      setStatus("ZIP export failed: " + err.message);
    }
  };

  // Lock / Unlock
  const handleLock = () => {
    if (!lockPassword) return;
    setLocked(true);
    setShowLockModal(false);
    setStatus("🔒 Document locked");
  };
  const handleUnlock = () => {
    if (unlockInput === lockPassword) {
      setLocked(false);
      setShowUnlockModal(false);
      setUnlockInput("");
      setUnlockError("");
      setStatus("🔓 Document unlocked");
    } else setUnlockError("Incorrect password");
  };

  const deleteSelected = () => {
    if (!selected) return;
    setAnnotations((p) => ({
      ...p,
      [currentPage]: (p[currentPage] || []).filter(
        (a) => a.id !== selected.annId,
      ),
    }));
    setSelected(null);
  };

  // Remove active page from view (page stays on server)
  const handleDeletePage = () => {
    if (!pages.length) return;
    const i = currentPage;
    if (pages.length === 1) {
      setPages([]);
      setAnnotations({});
      setCurrentPage(0);
      setSelected(null);
      setStatus("Upload a PDF to start editing");
      return;
    }
    const newPages = pages.filter((_, idx) => idx !== i);
    const newAnnotations = {};
    Object.entries(annotations).forEach(([k, v]) => {
      const ki = parseInt(k);
      if (ki === i) return;
      newAnnotations[ki > i ? ki - 1 : ki] = v;
    });
    setPages(newPages);
    setAnnotations(newAnnotations);
    setSelected(null);
    const newCurrent = Math.min(i, newPages.length - 1);
    setCurrentPage(newCurrent);
    setStatus(`Page ${i + 1} removed from view`);
  };

  const selectedAnn = selected
    ? (annotations[currentPage] || []).find((a) => a.id === selected.annId)
    : null;

  const cursors = {
    select: "default",
    text: "text",
    image: "crosshair",
    delete: "not-allowed",
    rect: "crosshair",
  };
  const freeLimit = user?.plan === "free" ? 5 : null;
  const editsLeft = freeLimit
    ? Math.max(0, freeLimit - (user?.monthlyEdits || 0))
    : null;

  const inp = {
    width: "100%",
    padding: "9px 12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "#e2e8f0",
    fontSize: 13,
    fontFamily: "'Sora',sans-serif",
    outline: "none",
  };

  return (
    <div
      style={{
        fontFamily: "'Sora',sans-serif",
        background: "#0a0c15",
        minHeight: "100vh",
        color: "#e2e8f0",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* TOP AD BANNER */}
      {user?.plan === "free" && (
        <div style={{ padding: "6px 16px", background: "#0d1020" }}>
          <AdSlot type="banner" />
        </div>
      )}

      {/* TOOLBAR */}
      <div
        style={{
          background: "#0d1020",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          padding: "0 14px",
          gap: 8,
          height: 58,
          flexShrink: 0,
          zIndex: 100,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontWeight: 800,
            fontSize: 16,
            background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginRight: 6,
            letterSpacing: "-0.5px",
          }}
        >
          📄 FreePDFMaster.in
        </div>

        <ToolBtn
          icon="⬆️"
          label={pdfJsReady ? "Upload PDF" : "Loading…"}
          onClick={() => fileInputRef.current.click()}
          disabled={uploading || !pdfJsReady}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
        <Sep />

        <ToolBtn
          icon="↖"
          label="Select"
          active={tool === "select"}
          onClick={() => setTool("select")}
          disabled={locked}
        />
        <ToolBtn
          icon="T"
          label="Text"
          active={tool === "text"}
          onClick={() => setTool("text")}
          disabled={locked}
        />
        <ToolBtn
          icon="🖼"
          label="Image"
          onClick={() => imgInputRef.current.click()}
          active={tool === "image"}
          disabled={locked}
        />
        <input
          ref={imgInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: "none" }}
        />
        <ToolBtn
          icon="⬛"
          label="Redact"
          active={tool === "rect"}
          onClick={() => setTool("rect")}
          disabled={locked}
        />
        <ToolBtn
          icon="🗑"
          label="Delete"
          active={tool === "delete"}
          danger
          onClick={() => setTool("delete")}
          disabled={locked}
        />
        <Sep />
        <ToolBtn
          icon="✂️"
          label="Del Page"
          onClick={handleDeletePage}
          disabled={!pages.length || locked}
          danger
        />
        <Sep />

        {(tool === "text" || selectedAnn?.type === "text") && (
          <>
            <input
              type="number"
              min={8}
              max={72}
              value={fontSize}
              onChange={(e) => setFontSize(+e.target.value)}
              style={{ ...inp, width: 52, padding: "5px 8px" }}
            />
            <input
              type="color"
              value={fontColor}
              onChange={(e) => setFontColor(e.target.value)}
              style={{
                width: 30,
                height: 28,
                border: "none",
                background: "none",
                cursor: "pointer",
              }}
            />
          </>
        )}

        {(tool === "rect" || selectedAnn?.type === "rect") && (
          <>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>Fill:</span>
            <input
              type="color"
              value={
                tool === "rect"
                  ? rectColor
                  : selectedAnn?.fillColor || "#000000"
              }
              onChange={(e) => {
                if (tool === "rect") setRectColor(e.target.value);
                if (selectedAnn?.type === "rect") {
                  setAnnotations((p) => ({
                    ...p,
                    [currentPage]: p[currentPage].map((a) =>
                      a.id === selectedAnn.id
                        ? { ...a, fillColor: e.target.value }
                        : a,
                    ),
                  }));
                }
              }}
              style={{
                width: 30,
                height: 28,
                border: "none",
                background: "none",
                cursor: "pointer",
              }}
            />
          </>
        )}
        <Sep />

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            onClick={() => setZoom((z) => Math.min(z + 0.15, 3))}
            style={{
              background: "linear-gradient(135deg,#3b82f6,#6366f1)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 22,
              fontWeight: 800,
              width: 38,
              height: 38,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(59,130,246,0.15)",
              transition: "background 0.2s",
            }}
            title="Zoom In"
          >
            +
          </button>
          <span
            style={{
              fontSize: 15,
              color: "#3b82f6",
              minWidth: 44,
              textAlign: "center",
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom((z) => Math.max(z - 0.15, 0.4))}
            style={{
              background: "linear-gradient(135deg,#3b82f6,#6366f1)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 22,
              fontWeight: 800,
              width: 38,
              height: 38,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(59,130,246,0.15)",
              transition: "background 0.2s",
            }}
            title="Zoom Out"
          >
            −
          </button>
        </div>
        <Sep />

        {locked ? (
          <ToolBtn
            icon="🔓"
            label="Unlock"
            onClick={() => setShowUnlockModal(true)}
          />
        ) : (
          <ToolBtn
            icon="🔒"
            label="Lock"
            onClick={() => setShowLockModal(true)}
            disabled={!pages.length}
          />
        )}

        {/* Export dropdown + Merge button */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <button
            disabled={!pages.length}
            onClick={() => setShowExportMenu((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 12px",
              background: pages.length
                ? "linear-gradient(135deg,#3b82f6,#6366f1)"
                : "rgba(255,255,255,0.04)",
              color: pages.length ? "#fff" : "#475569",
              border: "none",
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 700,
              cursor: pages.length ? "pointer" : "not-allowed",
              fontFamily: "'Sora',sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            💾 Export ▾
          </button>
          {showExportMenu && (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                top: "110%",
                right: 0,
                background: "#131929",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 10,
                boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                zIndex: 9999,
                minWidth: 210,
                overflow: "hidden",
              }}
            >
              <button
                onClick={handleExportImage}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "11px 16px",
                  background: "none",
                  border: "none",
                  color: "#e2e8f0",
                  fontSize: 13,
                  textAlign: "left",
                  cursor: "pointer",
                  fontFamily: "'Sora',sans-serif",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(59,130,246,0.15)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "none")
                }
              >
                🖼️ Export current page (PNG)
              </button>
              <div
                style={{ height: 1, background: "rgba(255,255,255,0.06)" }}
              />
              <button
                onClick={handleExportPdf}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "11px 16px",
                  background: "none",
                  border: "none",
                  color: "#e2e8f0",
                  fontSize: 13,
                  textAlign: "left",
                  cursor: "pointer",
                  fontFamily: "'Sora',sans-serif",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(59,130,246,0.15)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "none")
                }
              >
                📄 Export current page (PDF)
              </button>
              <div
                style={{ height: 1, background: "rgba(255,255,255,0.06)" }}
              />
              <button
                onClick={handleExportAllPdf}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "11px 16px",
                  background: "none",
                  border: "none",
                  color: "#e2e8f0",
                  fontSize: 13,
                  textAlign: "left",
                  cursor: "pointer",
                  fontFamily: "'Sora',sans-serif",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(59,130,246,0.15)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "none")
                }
              >
                📄 Export all pages (PDF)
              </button>
              <button
                onClick={handleExportAllZip}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "11px 16px",
                  background: "none",
                  border: "none",
                  color: "#e2e8f0",
                  fontSize: 13,
                  textAlign: "left",
                  cursor: "pointer",
                  fontFamily: "'Sora',sans-serif",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(59,130,246,0.15)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "none")
                }
              >
                🗂️ Export all pages (ZIP of PNGs)
              </button>
            </div>
          )}

          {/* Merge button */}
          <button
            disabled={documents.length < 2}
            onClick={handleMergeDocs}
            style={{
              marginLeft: 8,
              padding: "5px 12px",
              background:
                documents.length >= 2
                  ? "linear-gradient(135deg,#10b981,#6366f1)"
                  : "rgba(255,255,255,0.04)",
              color: documents.length >= 2 ? "#fff" : "#475569",
              border: "none",
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 700,
              cursor: documents.length >= 2 ? "pointer" : "not-allowed",
              fontFamily: "'Sora',sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            🔗 Merge PDFs
          </button>
        </div>

        {/* Plan badge */}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {editsLeft !== null && (
            <div
              style={{
                fontSize: 11,
                color: editsLeft <= 1 ? "#f87171" : "#64748b",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 6,
                padding: "4px 10px",
                whiteSpace: "nowrap",
              }}
            >
              {editsLeft} export{editsLeft !== 1 ? "s" : ""} left
            </div>
          )}
          <div style={{ fontSize: 11, color: "#475569" }}>{user?.name}</div>
          <button
            onClick={logout}
            style={{
              fontSize: 11,
              color: "#475569",
              background: "none",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 6,
              padding: "4px 10px",
              cursor: "pointer",
              fontFamily: "'Sora',sans-serif",
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* STATUS BAR */}
      <div
        style={{
          background: "#0b0e1c",
          borderBottom: "1px solid rgba(255,255,255,0.04)",
          padding: "4px 16px",
          fontSize: 11,
          color: "#475569",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>{status}</span>
        {locked && (
          <span style={{ color: "#f59e0b" }}>
            🔒 Document Locked — editing disabled
          </span>
        )}
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* SIDEBAR: Documents & Thumbnails */}
        <div
          style={{
            width: 220,
            background: "#0d1020",
            borderRight: "1px solid rgba(255,255,255,0.05)",
            overflowY: "auto",
            flexShrink: 0,
            padding: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ marginBottom: 10 }}>
            <div
              style={{
                color: "#64748b",
                fontSize: 11,
                marginBottom: 4,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              My Documents
            </div>
            {documents.length === 0 && (
              <div
                style={{
                  color: "#374151",
                  fontSize: 12,
                  textAlign: "center",
                  margin: "16px 0",
                }}
              >
                No PDFs uploaded
              </div>
            )}
            {documents.map((doc) => (
              <div
                key={doc.id}
                onClick={() => handleSwitchDoc(doc.id)}
                style={{
                  cursor: "pointer",
                  background:
                    currentDoc === doc.id
                      ? "linear-gradient(135deg,#3b82f6,#6366f1)"
                      : "#12172a",
                  color: currentDoc === doc.id ? "#fff" : "#cbd5e1",
                  borderRadius: 7,
                  padding: "7px 10px",
                  marginBottom: 4,
                  fontWeight: 600,
                  fontSize: 13,
                  border:
                    currentDoc === doc.id
                      ? "2px solid #3b82f6"
                      : "2px solid rgba(255,255,255,0.06)",
                  transition: "all 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <span style={{ fontSize: 15 }}>📄</span>
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {doc.name}
                </span>
                {currentDoc === doc.id && (
                  <span style={{ fontSize: 11, color: "#fbbf24" }}>Active</span>
                )}
                <button
                  title="Remove from view (file stays on server)"
                  onClick={(e) => handleRemoveDoc(e, doc.id)}
                  style={{
                    background: "rgba(239,68,68,0.15)",
                    border: "none",
                    borderRadius: 4,
                    color: "#f87171",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1,
                    padding: "2px 5px",
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* Thumbnails for current document */}
          {pages.length === 0 && (
            <div
              style={{
                color: "#374151",
                fontSize: 12,
                textAlign: "center",
                marginTop: 32,
                lineHeight: 1.6,
              }}
            >
              Upload a PDF
              <br />
              to see pages here
            </div>
          )}
          {pages.map((pg, i) => (
            <div
              key={i}
              onClick={() => {
                commitText();
                setCurrentPage(i);
                setSelected(null);
              }}
              style={{
                cursor: "pointer",
                border: `2px solid ${currentPage === i ? "#3b82f6" : "rgba(255,255,255,0.06)"}`,
                borderRadius: 8,
                overflow: "hidden",
                background: "#12172a",
                position: "relative",
                transition: "border 0.15s",
              }}
            >
              <ThumbCanvas srcCanvas={pg.canvas} />
              <div
                style={{
                  position: "absolute",
                  bottom: 3,
                  right: 5,
                  fontSize: 9,
                  color: "#475569",
                  background: "rgba(0,0,0,0.6)",
                  borderRadius: 3,
                  padding: "1px 4px",
                }}
              >
                pg {i + 1}
              </div>
              {/* Delete page button */}
              <button
                title="Delete this page"
                onClick={(e) => {
                  e.stopPropagation();
                  if (pages.length === 1) {
                    // Last page — clear everything
                    setPages([]);
                    setAnnotations({});
                    setCurrentPage(0);
                    setSelected(null);
                    setStatus("Upload a PDF to start editing");
                    return;
                  }
                  const newPages = pages.filter((_, idx) => idx !== i);
                  // Remap annotations: shift keys above deleted index down by 1
                  const newAnnotations = {};
                  Object.entries(annotations).forEach(([k, v]) => {
                    const ki = parseInt(k);
                    if (ki === i) return; // deleted page
                    const newKey = ki > i ? ki - 1 : ki;
                    newAnnotations[newKey] = v;
                  });
                  setPages(newPages);
                  setAnnotations(newAnnotations);
                  setSelected(null);
                  setCurrentPage((prev) => Math.min(prev, newPages.length - 1));
                  setStatus(`Page ${i + 1} deleted`);
                }}
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  width: 20,
                  height: 20,
                  background: "rgba(239,68,68,0.85)",
                  border: "none",
                  borderRadius: 4,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  opacity: 0,
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
              >
                ✕
              </button>
            </div>
          ))}
          {user?.plan === "free" && pages.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <AdSlot type="rectangle" />
            </div>
          )}
        </div>

        {/* CANVAS AREA */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            background: "#0f1220",
            padding: 28,
            position: "relative",
          }}
        >
          {pages.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 20,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: 72,
                  filter: "drop-shadow(0 0 30px rgba(59,130,246,0.3))",
                }}
              >
                📄
              </div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Ready to edit your PDF
              </div>
              <div
                style={{
                  color: "#374151",
                  fontSize: 14,
                  maxWidth: 340,
                  lineHeight: 1.7,
                }}
              >
                Upload any PDF file to add text, insert images, delete content,
                and lock your document
              </div>
              <button
                onClick={() => fileInputRef.current.click()}
                style={{
                  padding: "13px 32px",
                  background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "'Sora',sans-serif",
                }}
              >
                ⬆️ Upload PDF File
              </button>
              {user?.plan === "free" && (
                <div style={{ fontSize: 12, color: "#374151" }}>
                  Free plan: {editsLeft} export{editsLeft !== 1 ? "s" : ""}{" "}
                  remaining this month
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                position: "relative",
                boxShadow: "0 12px 60px rgba(0,0,0,0.7)",
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.04)",
              }}
            >
              <canvas ref={pageCanvasRef} style={{ display: "block" }} />
              <canvas
                ref={overlayRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  cursor: cursors[tool] || "default",
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onDoubleClick={handleDblClick}
              />
              {editingText &&
                (() => {
                  const ann = (annotations[currentPage] || []).find(
                    (a) => a.id === editingText,
                  );
                  if (!ann) return null;
                  return (
                    <textarea
                      ref={textInputRef}
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onBlur={commitText}
                      onKeyDown={(e) => e.key === "Escape" && commitText()}
                      style={{
                        position: "absolute",
                        left: ann.x * zoom,
                        top: (ann.y - ann.fontSize) * zoom,
                        width: ann.width * zoom,
                        minHeight: (ann.height + 12) * zoom,
                        font: `${ann.fontSize * zoom}px Georgia,serif`,
                        color: ann.color,
                        background: "rgba(255,255,255,0.94)",
                        border: "2px solid #3b82f6",
                        borderRadius: 4,
                        padding: "2px 5px",
                        resize: "none",
                        outline: "none",
                        zIndex: 50,
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                      }}
                    />
                  );
                })()}
            </div>
          )}
        </div>

        {/* RIGHT PANEL: Properties + Ad */}
        <div
          style={{
            width: 188,
            background: "#0d1020",
            borderLeft: "1px solid rgba(255,255,255,0.05)",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
          }}
        >
          {selectedAnn && (
            <div
              style={{
                padding: 14,
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#3b82f6",
                  marginBottom: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Properties
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>
                Type: {selectedAnn.type}
              </div>
              <div style={{ fontSize: 11, color: "#374151", marginBottom: 10 }}>
                {Math.round(selectedAnn.x)},{" "}
                {Math.round(
                  selectedAnn.type === "text"
                    ? selectedAnn.y - selectedAnn.fontSize
                    : selectedAnn.y,
                )}{" "}
                · {Math.round(selectedAnn.width)}×
                {Math.round(selectedAnn.height)}
              </div>
              {selectedAnn.type === "text" && (
                <>
                  <div
                    style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}
                  >
                    Font Size
                  </div>
                  <input
                    type="number"
                    min={8}
                    max={72}
                    value={selectedAnn.fontSize}
                    onChange={(e) =>
                      setAnnotations((p) => ({
                        ...p,
                        [currentPage]: p[currentPage].map((a) =>
                          a.id === selectedAnn.id
                            ? { ...a, fontSize: +e.target.value }
                            : a,
                        ),
                      }))
                    }
                    style={{ ...inp, marginBottom: 10 }}
                  />
                  <div
                    style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}
                  >
                    Color
                  </div>
                  <input
                    type="color"
                    value={selectedAnn.color}
                    onChange={(e) =>
                      setAnnotations((p) => ({
                        ...p,
                        [currentPage]: p[currentPage].map((a) =>
                          a.id === selectedAnn.id
                            ? { ...a, color: e.target.value }
                            : a,
                        ),
                      }))
                    }
                    style={{
                      width: "100%",
                      height: 32,
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      marginBottom: 10,
                    }}
                  />
                  <button
                    onClick={() => {
                      setEditingText(selectedAnn.id);
                      setEditingValue(selectedAnn.text);
                      setTimeout(() => textInputRef.current?.focus(), 50);
                    }}
                    style={{
                      ...inp,
                      cursor: "pointer",
                      marginBottom: 8,
                      color: "#93c5fd",
                      textAlign: "center",
                    }}
                  >
                    ✏️ Edit Text
                  </button>
                </>
              )}
              {selectedAnn.type === "rect" && (
                <>
                  <div
                    style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}
                  >
                    Fill Color
                  </div>
                  <input
                    type="color"
                    value={selectedAnn.fillColor || "#000000"}
                    onChange={(e) =>
                      setAnnotations((p) => ({
                        ...p,
                        [currentPage]: p[currentPage].map((a) =>
                          a.id === selectedAnn.id
                            ? { ...a, fillColor: e.target.value }
                            : a,
                        ),
                      }))
                    }
                    style={{
                      width: "100%",
                      height: 32,
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      marginBottom: 10,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      lineHeight: 1.5,
                      marginBottom: 8,
                    }}
                  >
                    Drag corners to resize. Use black to redact content.
                  </div>
                </>
              )}
              <button
                onClick={deleteSelected}
                style={{
                  ...inp,
                  cursor: "pointer",
                  color: "#f87171",
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  textAlign: "center",
                }}
              >
                🗑️ Delete
              </button>
            </div>
          )}

          {pages.length > 0 && (
            <div
              style={{
                padding: 14,
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#3b82f6",
                  marginBottom: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Page {currentPage + 1}
              </div>
              <button
                onClick={handleDeletePage}
                disabled={locked}
                style={{
                  ...inp,
                  cursor: locked ? "not-allowed" : "pointer",
                  color: "#f87171",
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  textAlign: "center",
                  opacity: locked ? 0.4 : 1,
                }}
              >
                🗑️ Delete Page
              </button>
            </div>
          )}

          {user?.plan === "free" && (
            <div
              style={{
                padding: 14,
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <div
                style={{
                  background:
                    "linear-gradient(135deg,rgba(59,130,246,0.1),rgba(99,102,241,0.1))",
                  border: "1px solid rgba(59,130,246,0.2)",
                  borderRadius: 10,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: "#93c5fd",
                    marginBottom: 6,
                  }}
                >
                  🚀 Go Pro
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#475569",
                    lineHeight: 1.6,
                    marginBottom: 10,
                  }}
                >
                  Unlimited exports, no ads, larger files
                </div>
                <button
                  onClick={() => setShowUpgrade(true)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 7,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "'Sora',sans-serif",
                  }}
                >
                  Upgrade $9/mo
                </button>
              </div>
            </div>
          )}

          {user?.plan === "free" && (
            <div style={{ padding: 10, flex: 1 }}>
              <AdSlot type="rectangle" />
            </div>
          )}
        </div>
      </div>

      {/* CROP MODAL */}
      {cropImage && (
        <Modal
          title="✂️ Crop & Adjust Image"
          onClose={() => setCropImage(null)}
        >
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
            Drag to select crop area, or skip for full image
          </div>
          <canvas
            ref={cropCanvasRef}
            style={{
              cursor: "crosshair",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 6,
              maxWidth: "100%",
              display: "block",
            }}
            onMouseDown={(e) => {
              const r = cropCanvasRef.current.getBoundingClientRect();
              cropMouseDownRef.current = {
                x: e.clientX - r.left,
                y: e.clientY - r.top,
              };
              setCropDragging(true);
            }}
            onMouseMove={(e) => {
              if (!cropDragging || !cropMouseDownRef.current) return;
              const r = cropCanvasRef.current.getBoundingClientRect();
              const x2 = e.clientX - r.left,
                y2 = e.clientY - r.top;
              const { x, y } = cropMouseDownRef.current;
              setCropRect({
                x: Math.min(x, x2),
                y: Math.min(y, y2),
                w: Math.abs(x2 - x),
                h: Math.abs(y2 - y),
              });
            }}
            onMouseUp={() => setCropDragging(false)}
          />
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 14,
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={() => setCropImage(null)}
              style={{
                ...inp,
                width: "auto",
                padding: "9px 18px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={applyCrop}
              style={{
                padding: "9px 20px",
                background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'Sora',sans-serif",
              }}
            >
              Apply & Insert
            </button>
          </div>
        </Modal>
      )}

      {/* LOCK MODAL */}
      {showLockModal && (
        <Modal title="🔒 Lock Document" onClose={() => setShowLockModal(false)}>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
            Set a password to prevent editing until unlocked.
          </div>
          <input
            type="password"
            placeholder="Enter password"
            value={lockPassword}
            onChange={(e) => setLockPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLock()}
            autoFocus
            style={inp}
          />
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 14,
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={() => setShowLockModal(false)}
              style={{
                ...inp,
                width: "auto",
                padding: "9px 18px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleLock}
              disabled={!lockPassword}
              style={{
                padding: "9px 20px",
                background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'Sora',sans-serif",
                opacity: lockPassword ? 1 : 0.5,
              }}
            >
              Lock
            </button>
          </div>
        </Modal>
      )}

      {/* UNLOCK MODAL */}
      {showUnlockModal && (
        <Modal
          title="🔓 Unlock Document"
          onClose={() => {
            setShowUnlockModal(false);
            setUnlockInput("");
            setUnlockError("");
          }}
        >
          <input
            type="password"
            placeholder="Enter password"
            value={unlockInput}
            onChange={(e) => {
              setUnlockInput(e.target.value);
              setUnlockError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleUnlock()}
            autoFocus
            style={{ ...inp, marginBottom: 6 }}
          />
          {unlockError && (
            <div style={{ color: "#f87171", fontSize: 12, marginBottom: 10 }}>
              {unlockError}
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 10,
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={() => {
                setShowUnlockModal(false);
                setUnlockInput("");
                setUnlockError("");
              }}
              style={{
                ...inp,
                width: "auto",
                padding: "9px 18px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleUnlock}
              style={{
                padding: "9px 20px",
                background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'Sora',sans-serif",
              }}
            >
              Unlock
            </button>
          </div>
        </Modal>
      )}

      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </div>
  );
}
